// src/pages/ProfilePage.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useDoorloopjes } from '../context/DoorloopjesContext';
import SchoolSearchField from '../components/SchoolSearchField';
import HerinneringenVoorkeur from '../components/HerinneringenVoorkeur';
import { wisToken } from '../utils/videoVerificatie';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;

function ProfilePage() {
  const { currentUser, loadInitialData } = useDoorloopjes();
  const navigate = useNavigate();
  const [message, setMessage] = useState('');

  // Profielgegevens state
  const [formData, setFormData] = useState({
    fullName: '',
    educationRole: '',
    educationRoleOther: '',
    educationSector: '',
    isStudent: false,
    schoolName: '',
    city: '',
    brin: '',
    opleiding: '',
  });

  // Wachtwoordwijziging state
  const [pwOld, setPwOld] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwNew2, setPwNew2] = useState('');
  const [pwMsg, setPwMsg] = useState('');

  useEffect(() => {
    if (currentUser) {
      setFormData({
        fullName: currentUser.fullName || '',
        educationRole: currentUser.educationRole || '',
        educationRoleOther: currentUser.educationRoleOther || '',
        educationSector: currentUser.educationSector || '',
        isStudent: currentUser.isStudent || false,
        schoolName: currentUser.schoolName || '',
        city: currentUser.city || '',
        brin: currentUser.brin || '',
        opleiding: currentUser.opleiding || '',
      });
    }
  }, [currentUser]);

  const handleLogout = () => {
    localStorage.removeItem('jwt');
    wisToken();
    loadInitialData();
    navigate('/');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSchoolChange = (patch) => {
    setFormData((prev) => ({ ...prev, ...patch }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const jwt = localStorage.getItem('jwt');
    if (!jwt || !currentUser) return;

    const dataToSubmit = {
      ...formData,
      educationRoleOther:
        formData.educationRole === 'anders' ? formData.educationRoleOther : '',
    };

    try {
      await axios.put(
        `${STRAPI_URL}/api/users/${currentUser.id}`,
        dataToSubmit,
        { headers: { Authorization: `Bearer ${jwt}` } }
      );
      setMessage('Profiel succesvol bijgewerkt!');
      await loadInitialData();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Fout bij bijwerken profiel:', error);
      setMessage('Opslaan lukte niet. Probeer het opnieuw.');
    }
  };

  const handleChangePassword = async () => {
    setPwMsg('');
    if (!pwOld || !pwNew || pwNew !== pwNew2) {
      setPwMsg('Controleer de velden (nieuw wachtwoord moet overeenkomen).');
      return;
    }
    try {
      const jwt = localStorage.getItem('jwt');
      await axios.post(
        `${STRAPI_URL}/api/auth/change-password`,
        {
          currentPassword: pwOld,
          password: pwNew,
          passwordConfirmation: pwNew2,
        },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );
      setPwMsg('Wachtwoord succesvol gewijzigd.');
      setPwOld('');
      setPwNew('');
      setPwNew2('');
    } catch (e) {
      console.error('Fout bij wijzigen wachtwoord:', e);
      setPwMsg('Wijzigen lukte niet. Klopt je huidige wachtwoord?');
    }
  };

  if (!currentUser) {
    return <p>Profiel wordt geladen...</p>;
  }

  return (
    <div className="profile-page-container">
      <header className="profile-page-header">
        <h1>Mijn Profiel</h1>
        <button onClick={handleLogout} className="btn-logout">
          Uitloggen
        </button>
      </header>

      <main className="profile-content-grid">
        <section className="profile-details-section">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>E-mailadres</label>
              <input type="email" value={currentUser.email} disabled />
            </div>
            <div className="form-group">
              <label htmlFor="fullName">Volledige naam</label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="educationRole">Rol in het onderwijs</label>
              <select
                id="educationRole"
                name="educationRole"
                value={formData.educationRole}
                onChange={handleInputChange}
                required
              >
                <option value="leraar">Leraar</option>
                <option value="schoolleider">Schoolleider</option>
                <option value="bestuurder">Bestuurder</option>
                <option value="anders">Anders</option>
              </select>
            </div>
            {formData.educationRole === 'anders' && (
              <div className="form-group">
                <label htmlFor="educationRoleOther">Anders, namelijk</label>
                <input
                  type="text"
                  id="educationRoleOther"
                  name="educationRoleOther"
                  value={formData.educationRoleOther}
                  onChange={handleInputChange}
                  required
                />
              </div>
            )}
            <div className="form-group">
              <label htmlFor="educationSector">Onderwijssector</label>
              <select
                id="educationSector"
                name="educationSector"
                value={formData.educationSector}
                onChange={handleInputChange}
                required
              >
                <option value="PO">PO</option>
                <option value="VO">VO</option>
                <option value="MBO">MBO</option>
                <option value="HBO">HBO</option>
                <option value="S(VO)">S(VO)</option>
              </select>
            </div>
            <div
              className="form-group"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <input
                id="isStudent"
                name="isStudent"
                type="checkbox"
                checked={formData.isStudent}
                onChange={handleInputChange}
                style={{ width: 'auto' }}
              />
              <label htmlFor="isStudent" style={{ marginBottom: 0 }}>
                Ik ben een student
              </label>
            </div>
            <SchoolSearchField
              brin={formData.brin}
              schoolName={formData.schoolName}
              city={formData.city}
              opleiding={formData.opleiding}
              educationSector={formData.educationSector}
              onChange={handleSchoolChange}
              idPrefix="profile"
            />

            {message && <p className="profile-message">{message}</p>}
            <button type="submit" className="btn-save-changes">
              Wijzigingen Opslaan
            </button>
          </form>
        </section>

        {/* Nieuwe sectie: wachtwoord wijzigen */}
        <section className="profile-details-section" style={{ marginTop: '1.5rem' }}>
          <h3>Wachtwoord wijzigen</h3>
          <div className="form-group">
            <label>Huidig wachtwoord</label>
            <input
              type="password"
              value={pwOld}
              onChange={(e) => setPwOld(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Nieuw wachtwoord</label>
            <input
              type="password"
              value={pwNew}
              onChange={(e) => setPwNew(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Bevestig nieuw wachtwoord</label>
            <input
              type="password"
              value={pwNew2}
              onChange={(e) => setPwNew2(e.target.value)}
            />
          </div>
          {pwMsg && <p className="profile-message">{pwMsg}</p>}
          <button className="btn-save-changes" onClick={handleChangePassword}>
            Wachtwoord wijzigen
          </button>
        </section>

        <HerinneringenVoorkeur />
      </main>
    </div>
  );
}

export default ProfilePage;
